import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import PageHeader from '../components/ui/PageHeader.jsx';
import StatusBadge from '../components/ui/StatusBadge.jsx';
import ClientForm from './ClientForm.jsx';
import api from '../lib/api.js';

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showEdit, setShowEdit] = useState(false);
  const { data, loading, error } = useApi('/clients/' + id);
  const client = data?.data;
  async function handleArchive() {
    if (!confirm('Archiviare questo cliente?')) return;
    const res = await api.del('/clients/' + id);
    if (res.ok) navigate('/clients'); else alert(res.error || 'Errore archiviazione');
  }
  if (loading) return <div className="text-surface-4 py-8 text-center">Caricamento...</div>;
  if (error || !client) return <div className="p-4 rounded-md bg-danger/10 text-danger">Errore: {error || 'non trovato'}</div>;
  return (
    <>
      <PageHeader
        title={client.display_name || client.legal_name}
        subtitle={client.client_type === 'company' ? 'Azienda' : 'Persona fisica'}
        action={
          <div className="flex gap-2">
            <button className="btn btn-secondary text-xs" onClick={handleArchive}>Archivia</button>
            <button className="btn btn-primary text-xs" onClick={() => setShowEdit((v) => !v)}>
              {showEdit ? 'Annulla modifica' : 'Modifica'}
            </button>
          </div>
        }
      />
      {showEdit && (
        <div className="card-elevated mb-4">
          <ClientForm clientId={Number(id)} onSaved={() => { setShowEdit(false); window.location.reload(); }} onCancel={() => setShowEdit(false)} />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-elevated space-y-4">
          <InfoGrid label="Anagrafica" rows={[
            ['Ragione sociale', client.legal_name],
            ['Tipo', client.client_type === 'company' ? 'Azienda' : 'Persona fisica'],
            ['Nome', client.first_name], ['Cognome', client.last_name],
            ['Stato', <StatusBadge status={client.status} small />],
            ['Fonte', client.source],
          ]} />
          <InfoGrid label="Fiscale & Contatti" rows={[
            ['Partita IVA', client.vat_number], ['Codice Fiscale', client.tax_code],
            ['Email', client.email], ['PEC', client.pec],
            ['Telefono', client.phone], ['Cellulare', client.mobile],
            ['SdI Code', client.sdi_code], ['Website', client.website],
          ]} />

          <div>
            <h3 className="field-label">Indirizzo</h3>
            <div className="text-sm text-surface-2">
              {client.street && <div>{client.street}</div>}
              <div>{client.zip && <span>{client.zip} </span>}{client.city && <span>{client.city}</span>}{client.province && <span> ({client.province})</span>}</div>
              {client.country && <div className="text-surface-4">{client.country}</div>}
            </div>
          </div>
          {client.tags?.length > 0 && (
            <div>
              <h3 className="field-label">Tag</h3>
              <div className="flex flex-wrap gap-2">
                {client.tags.map((t) => (
                  <span key={t.id} className="badge" style={{ background: t.color + '20', color: t.color, borderColor: t.color + '40' }}>{t.name}</span>
                ))}
              </div>
            </div>
          )}
          {client.notes && (
            <div>
              <h3 className="field-label">Note</h3>
              <p className="text-sm text-surface-2 whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div className="card-elevated">
            <h3 className="field-label">Informazioni</h3>
            <div className="text-xs text-surface-4 space-y-2">
              <div><span className="text-surface-3 font-medium">Creato: </span>{new Date(client.created_at).toLocaleDateString('it-IT')}</div>
              <div><span className="text-surface-3 font-medium">Aggiornato: </span>{new Date(client.updated_at).toLocaleDateString('it-IT')}</div>
              <div><span className="text-surface-3 font-medium">ID: </span><code className="text-brand-300">{client.id}</code></div>
            </div>
          </div>
          {client.quotes?.length > 0 && (
            <div className="card-elevated">
              <h3 className="field-label">Preventivi ({client.quotes.length})</h3>
              <table className="table-root">
                <thead>
                  <tr>
                    <th className="table-header">N°</th>
                    <th className="table-header">Data</th>
                    <th className="table-header">Stato</th>
                    <th className="table-header text-right">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {client.quotes.map((q) => (
                    <tr key={q.id} className="cursor-pointer hover:bg-line/30" onClick={() => navigate('/preventivi')}>
                      <td className="table-cell font-mono text-brand-300 text-xs">{q.number}</td>
                      <td className="table-cell text-xs">{new Date(q.issue_date).toLocaleDateString('it-IT')}</td>
                      <td className="table-cell"><StatusBadge status={q.status} small /></td>
                      <td className="table-cell text-right font-mono text-xs">&euro; {Number(q.total_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="card-elevated">
            <h3 className="field-label">Azioni rapide</h3>
            <div className="space-y-2">
              <button className="btn btn-primary w-full text-sm" onClick={() => navigate('/preventivi')}>+ Nuovo preventivo</button>
              <button className="btn btn-secondary w-full text-sm" onClick={() => navigate('/listino')}>Vai al listino</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function InfoGrid({ label, rows }) {
  return (
    <div>
      <h3 className="field-label">{label}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        {rows.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v], i) => (
          <div key={i} className="flex flex-col">
            <span className="text-xs text-surface-4">{k}</span>
            <span className="text-sm text-surface">{typeof v === 'object' ? null : String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

